// Date formatting utilities using Day.js

import dayjs, { type Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getAddressFromCacheById } from '@/domains/address/repositories/addressRepository';
import { findIana } from 'windows-iana';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export const getClientTimezoneOffsetMinutes = (): number => dayjs().utcOffset();

type DateLike = string | number | Date | Dayjs | null | undefined;

const toDayjs = (value: DateLike): Dayjs | null => {
  if (value == null) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

export const useDateFormat = () => {
  const toLocalIso = (d = new Date()) => {
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, -1);
  };

  const toIana = (windowsTz?: string | null): string | null => {
    if (!windowsTz) return null;
    try {
      const matches = findIana(windowsTz);
      // windows-iana can return multiple; prefer the first IANA entry
      return matches?.[0]?.iana?.[0] ?? null;
    } catch {
      return null;
    }
  };

  const utcNow = (): string => dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSS');

  const utcNowDate = (): Date => dayjs().utc().toDate();

  /**
   * Formats a UTC instant as wall-clock time in the destination address timezone.
   * Pass a Date from utcNowDate() or an ISO string that represents a UTC instant (e.g. ends with Z).
   */
  const convertToAddressLocalTimeByAddressId = async (
    utcInstant: string | Date,
    addressId: number,
    getTimeZoneById?: (id: number) => { code?: string } | null
  ): Promise<string> => {
    const utc = dayjs.utc(utcInstant);
    const address = await getAddressFromCacheById(addressId);
    if (!address?.timeZoneId) {
      return utc.tz(dayjs.tz.guess()).format('YYYY-MM-DDTHH:mm:ss.SSS');
    }

    let timeZoneCode: string | null = null;
    if (getTimeZoneById) {
      try {
        const timeZone = getTimeZoneById(address.timeZoneId);
        timeZoneCode = timeZone?.code ?? null;
      } catch {
        timeZoneCode = null;
      }
    }

    const iana = toIana(timeZoneCode) ?? dayjs.tz.guess();
    return utc.tz(iana).format('YYYY-MM-DDTHH:mm:ss.SSS');
  };

  const formatDateTime = (dateString: DateLike, fallback = 'No date available'): string => {
    const date = toDayjs(dateString);
    return date ? date.format('MM/DD/YYYY hh:mm A') : fallback;
  };

  const formatDate = (dateString: DateLike, fallback = 'No date available'): string => {
    const date = toDayjs(dateString);
    return date ? date.format('MM/DD/YYYY') : fallback;
  };

  const formatTime = (dateString: DateLike, fallback = 'No time available'): string => {
    const date = toDayjs(dateString);
    return date ? date.format('hh:mm A') : fallback;
  };

  const formatTimeSpan = (timeSpanString: string, fallback = 'No time available'): string => {
    if (!timeSpanString) return fallback;

    // Handle TimeSpan format "HH:mm:ss" or "HH:mm"
    const timeRegex = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;
    const timeMatch = timeRegex.exec(timeSpanString);
    if (timeMatch?.[1] && timeMatch?.[2]) {
      const hours = Number.parseInt(timeMatch[1], 10);
      const minutes = Number.parseInt(timeMatch[2], 10);

      // Create a date object for today with the specified time
      const today = dayjs().startOf('day');
      const timeDate = today.hour(hours).minute(minutes);

      return timeDate.format('h:mm A');
    }

    return fallback;
  };

  const formatCustom = (
    dateString: DateLike,
    format: string,
    fallback = 'No date available'
  ): string => {
    const date = toDayjs(dateString);
    return date ? date.format(format) : fallback;
  };

  const isValidDate = (dateString: DateLike): boolean => {
    return !!toDayjs(dateString);
  };

  const formatRelative = (dateString: DateLike, fallback = 'Unknown time'): string => {
    const date = toDayjs(dateString);
    return date ? date.fromNow() : fallback;
  };

  return {
    actions: {
      formatDateTime,
      formatDate,
      formatTime,
      formatTimeSpan,
      formatCustom,
      isValidDate,
      formatRelative,
      toLocalIso,
      utcNow,
      utcNowDate,
      convertToAddressLocalTimeByAddressId,
    },
  };
};
